/**
 * Reviews API Client
 * Communicates with reviews-api.wadelo.com
 */
(function () {
  'use strict';

  const API_BASE = 'https://reviews-api.wadelo.com';

  window.__ReviewsAPI = {
    /**
     * Fetch reviews for a product
     * @param {string} productId — Shopify numeric product ID
     * @returns {Promise<{reviews: Array, count: number, average: number}>}
     */
    async getProductReviews(productId) {
      const res = await fetch(`${API_BASE}/reviews?product_id=${encodeURIComponent(productId)}`);
      if (!res.ok) throw new Error('Failed to fetch reviews');
      return res.json();
    },

    /**
     * Fetch homepage-visible reviews
     * @returns {Promise<{reviews: Array}>}
     */
    async getHomepageReviews() {
      const res = await fetch(`${API_BASE}/reviews/homepage`);
      if (!res.ok) throw new Error('Failed to fetch homepage reviews');
      return res.json();
    },

    /**
     * Submit a new review
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async submitReview(data) {
      const res = await fetch(`${API_BASE}/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to submit review');
      return json;
    },
  };

  // -----------------------------------------------------------------------
  // Render helpers
  // -----------------------------------------------------------------------

  function renderStars(rating, size) {
    const sz = size === 'small' ? 14 : size === 'large' ? 22 : 18;
    let html = `<span class="review-stars review-stars--${size || 'medium'}" aria-label="${rating} sur 5 étoiles">`;
    for (let i = 1; i <= 5; i++) {
      if (i <= rating) {
        html += `<svg class="review-star review-star--filled" width="${sz}" height="${sz}" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M10 1.5L12.39 6.9L18.18 7.44L13.83 11.29L15.18 17L10 13.77L4.82 17L6.17 11.29L1.82 7.44L7.61 6.9L10 1.5Z" fill="currentColor"/></svg>`;
      } else {
        html += `<svg class="review-star review-star--empty" width="${sz}" height="${sz}" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M10 1.5L12.39 6.9L18.18 7.44L13.83 11.29L15.18 17L10 13.77L4.82 17L6.17 11.29L1.82 7.44L7.61 6.9L10 1.5Z" stroke="currentColor" stroke-width="1" fill="none"/></svg>`;
      }
    }
    html += '</span>';
    return html;
  }

  function formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }

  function escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function renderVerifiedBadge() {
    return `<span class="product-reviews__verified-badge"><svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M10 0L12.2 3.6L16.18 2.5L15.68 6.6L19.51 8.54L16.8 11.6L18.09 15.56L14.02 15.56L12.2 19.14L10 16L7.8 19.14L5.98 15.56L1.91 15.56L3.2 11.6L0.49 8.54L4.32 6.6L3.82 2.5L7.8 3.6L10 0Z" fill="currentColor"/><path d="M7 10L9 12L13 8" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg> Achat vérifié</span>`;
  }

  function renderReviewCard(review, pinned) {
    const pinnedClass = pinned ? ' product-reviews__item--pinned' : '';
    let html = `<div class="product-reviews__item${pinnedClass}">`;
    html += `<div class="product-reviews__item-header">`;
    html += `<div class="product-reviews__item-rating">${renderStars(review.rating, 'small')}</div>`;
    html += `<div class="product-reviews__item-meta">`;
    html += `<span class="product-reviews__item-author">${escapeHtml(review.author_name)}</span>`;
    if (review.verified_badge) html += renderVerifiedBadge();
    html += `</div></div>`;
    if (review.title) html += `<h3 class="product-reviews__item-title">${escapeHtml(review.title)}</h3>`;
    html += `<div class="product-reviews__item-body">${escapeHtml(review.body)}</div>`;
    html += `<div class="product-reviews__item-date">${formatDate(review.review_date)}</div>`;
    if (pinned) html += `<span class="product-reviews__pin-label">Épinglé</span>`;
    html += `</div>`;
    return html;
  }

  // Carte d'avis "boutique" (fallback) : comme une carte normale + lien vers l'article concerné
  function renderFallbackCard(review) {
    let html = `<div class="product-reviews__item">`;
    html += `<div class="product-reviews__item-header">`;
    html += `<div class="product-reviews__item-rating">${renderStars(review.rating, 'small')}</div>`;
    html += `<div class="product-reviews__item-meta">`;
    html += `<span class="product-reviews__item-author">${escapeHtml(review.author_name)}</span>`;
    if (review.verified_badge) html += renderVerifiedBadge();
    html += `</div></div>`;
    if (review.title) html += `<h3 class="product-reviews__item-title">${escapeHtml(review.title)}</h3>`;
    html += `<div class="product-reviews__item-body">${escapeHtml(review.body)}</div>`;
    html += `<div class="product-reviews__item-footer-row">`;
    html += `<span class="product-reviews__item-date">${formatDate(review.review_date)}</span>`;
    if (review.product_handle) {
      html += `<a href="/products/${escapeHtml(review.product_handle)}" class="product-reviews__item-product-link">Voir l'article</a>`;
    }
    html += `</div>`;
    html += `</div>`;
    return html;
  }

  // -----------------------------------------------------------------------
  // Product page — load reviews
  // -----------------------------------------------------------------------
  window.__ReviewsAPI.renderProductReviews = async function (container, productId) {
    if (!container || !productId) return;

    const listEl = container.querySelector('[data-reviews-list]');
    const summaryEl = container.querySelector('[data-reviews-summary]');
    const emptyEl = container.querySelector('[data-reviews-empty]');
    const countEl = container.querySelector('[data-reviews-count]');
    const avgNumEl = container.querySelector('[data-reviews-avg-number]');
    const avgStarsEl = container.querySelector('[data-reviews-avg-stars]');
    const mainTitleEl = container.querySelector('[data-reviews-main-title]');
    const fallbackEl = container.querySelector('[data-reviews-fallback]');
    const fallbackListEl = container.querySelector('[data-reviews-fallback-list]');
    const moreBtn = container.querySelector('[data-reviews-more]');

    try {
      const data = await window.__ReviewsAPI.getProductReviews(productId);

      if (data.count === 0) {
        // Aucun avis sur CE produit → on tente le fallback "avis boutique"
        if (summaryEl) summaryEl.style.display = 'none';
        if (listEl) listEl.innerHTML = '';
        if (emptyEl) emptyEl.style.display = 'none';

        let storeReviews = [];
        try {
          const store = await window.__ReviewsAPI.getHomepageReviews();
          storeReviews = store && store.reviews ? store.reviews : [];
        } catch (e) {
          storeReviews = [];
        }

        // Sécurité : ne jamais inclure un avis du produit courant
        storeReviews = storeReviews.filter(function (r) {
          return String(r.product_id) !== String(productId);
        });

        if (storeReviews.length > 0 && fallbackEl && fallbackListEl) {
          if (mainTitleEl) mainTitleEl.style.display = 'none';

          const INITIAL = 3;
          const firstReviews = storeReviews.slice(0, INITIAL);
          const restReviews = storeReviews.slice(INITIAL);

          fallbackListEl.innerHTML = firstReviews.map(renderFallbackCard).join('');

          if (moreBtn) {
            if (restReviews.length > 0) {
              moreBtn.style.display = '';
              moreBtn.onclick = function () {
                fallbackListEl.insertAdjacentHTML('beforeend', restReviews.map(renderFallbackCard).join(''));
                moreBtn.style.display = 'none';
              };
            } else {
              moreBtn.style.display = 'none';
            }
          }

          fallbackEl.style.display = '';
        } else {
          // Aucun avis boutique non plus → on n'affiche rien (titre + message masqués)
          if (mainTitleEl) mainTitleEl.style.display = 'none';
          if (fallbackEl) fallbackEl.style.display = 'none';
        }
        return;
      }

      // CE produit a des avis → comportement classique (et on réinitialise le fallback)
      if (mainTitleEl) mainTitleEl.style.display = '';
      if (fallbackEl) fallbackEl.style.display = 'none';
      if (moreBtn) moreBtn.style.display = 'none';
      if (summaryEl) summaryEl.style.display = '';
      if (emptyEl) emptyEl.style.display = 'none';
      if (countEl) countEl.textContent = `${data.count} avis`;
      if (avgNumEl) avgNumEl.textContent = data.average.toFixed(1);
      if (avgStarsEl) avgStarsEl.innerHTML = renderStars(Math.round(data.average), 'large');

      // List
      if (listEl) {
        let html = '';
        for (const r of data.reviews) {
          html += renderReviewCard(r, r.pinned);
        }
        listEl.innerHTML = html;
      }
    } catch (err) {
      console.error('Reviews load error:', err);
    }
  };

  // -----------------------------------------------------------------------
  // Product page — form submission
  // -----------------------------------------------------------------------
  window.__ReviewsAPI.initForm = function (form) {
    if (!form) return;
    form.addEventListener('submit', async function (e) {
      e.preventDefault();

      const btn = form.querySelector('[data-reviews-submit]');
      const successEl = form.closest('.product-reviews__form-section')?.querySelector('[data-reviews-form-success]');
      const errorEl = form.closest('.product-reviews__form-section')?.querySelector('[data-reviews-form-error]');

      if (btn) { btn.disabled = true; btn.textContent = 'Envoi...'; }
      if (successEl) successEl.style.display = 'none';
      if (errorEl) errorEl.style.display = 'none';

      try {
        const formData = new FormData(form);
        const data = {
          product_id: formData.get('product_id'),
          product_handle: formData.get('product_handle'),
          author_name: formData.get('author_name'),
          author_email: formData.get('author_email'),
          rating: parseInt(formData.get('rating')),
          title: formData.get('title'),
          body: formData.get('body'),
          verified_badge: formData.get('verified_badge') === 'true',
        };

        const result = await window.__ReviewsAPI.submitReview(data);

        if (successEl) {
          successEl.querySelector('p').textContent = result.message;
          successEl.style.display = '';
        }
        form.reset();
        // Re-check the 5-star default
        const fiveStar = form.querySelector('input[name="rating"][value="5"]');
        if (fiveStar) fiveStar.checked = true;
      } catch (err) {
        if (errorEl) {
          errorEl.querySelector('p').textContent = err.message || 'Une erreur est survenue.';
          errorEl.style.display = '';
        }
      } finally {
        if (btn) { btn.disabled = false; btn.textContent = 'Envoyer mon avis'; }
      }
    });
  };

  // -----------------------------------------------------------------------
  // Homepage — load & render marquee
  // -----------------------------------------------------------------------
  window.__ReviewsAPI.renderHomepageReviews = async function (container) {
    if (!container) return;

    try {
      const data = await window.__ReviewsAPI.getHomepageReviews();
      if (!data.reviews || data.reviews.length === 0) {
        container.style.display = 'none';
        return;
      }

      const track = container.querySelector('[data-reviews-track]');
      if (!track) return;

      function buildSlides(reviews) {
        let html = '';
        for (const r of reviews) {
          html += `<div class="homepage-reviews__card">`;
          html += `<div class="homepage-reviews__card-stars">${renderStars(r.rating, 'small')}</div>`;
          if (r.title) html += `<p class="homepage-reviews__card-title">${escapeHtml(r.title)}</p>`;
          const bodyTrunc = r.body && r.body.length > 180 ? r.body.substring(0, 177) + '...' : r.body;
          html += `<p class="homepage-reviews__card-body">${escapeHtml(bodyTrunc)}</p>`;
          html += `<div class="homepage-reviews__card-footer">`;
          html += `<span class="homepage-reviews__card-author">${escapeHtml(r.author_name)}</span>`;
          if (r.verified_badge) {
            html += `<span class="homepage-reviews__card-verified"><svg width="12" height="12" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M10 0L12.2 3.6L16.18 2.5L15.68 6.6L19.51 8.54L16.8 11.6L18.09 15.56L14.02 15.56L12.2 19.14L10 16L7.8 19.14L5.98 15.56L1.91 15.56L3.2 11.6L0.49 8.54L4.32 6.6L3.82 2.5L7.8 3.6L10 0Z" fill="currentColor"/><path d="M7 10L9 12L13 8" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg> Vérifié</span>`;
          }
          if (r.product_handle) {
            html += `<a href="/products/${escapeHtml(r.product_handle)}" class="homepage-reviews__card-product">Voir le produit</a>`;
          }
          html += `</div></div>`;
        }
        return html;
      }

      const slidesHtml = buildSlides(data.reviews);
      // Two copies for seamless loop
      track.innerHTML =
        `<div class="homepage-reviews__slides">${slidesHtml}</div>` +
        `<div class="homepage-reviews__slides" aria-hidden="true">${slidesHtml}</div>`;

      // Start marquee animation
      const speed = parseInt(container.dataset.speed) || 35;
      const direction = container.dataset.direction || 'normal';
      track.style.animationDuration = speed + 's';
      track.style.animationDirection = direction;

      container.addEventListener('mouseenter', () => { track.style.animationPlayState = 'paused'; });
      container.addEventListener('mouseleave', () => { track.style.animationPlayState = 'running'; });

    } catch (err) {
      console.error('Homepage reviews load error:', err);
    }
  };
})();
